import {
  Code,
  Flex,
  Tag,
  TagLabel,
  TagLeftIcon,
  Tooltip,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { PageTemplate } from '~/core/routes/dev/PageTemplate';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { TableBETA } from '~/packages/common-frontend/tables/TableBETA';
import { remToPx } from '~/packages/common-frontend/theme/utils';

// Constants
const COMPONENT_NAME = './common-frontend/tables/Table';

// Fragments
type MockTableData = {
  id: string;
  firstName: string;
  lastName: string;
  salary: number;
  email: string;
  clearance: 'top-secret' | 'secret' | 'civilian';
};

const columnHelper = createColumnHelper<MockTableData>();

const MOCK_TABLE_DATA: MockTableData[] = [
  {
    id: '2a6d981e-fdcd-4278-844c-244ea234d927',
    firstName: 'Gordon',
    lastName: 'Freeman',
    salary: 50000,
    email: 'gordon.freeman@mesa.mil.gov',
    clearance: 'secret',
  },
  {
    id: '9e4dd52a-bfda-4da8-970a-13b954e7ba66',
    firstName: 'G',
    lastName: 'Man',
    salary: 50000,
    email: '[redacted]',
    clearance: 'top-secret',
  },
  {
    id: 'f3927e42-9183-4950-925c-0229b27fa934',
    firstName: 'Eli',
    lastName: 'Vance',
    salary: 50000,
    email: 'eli.vance@mesa.mil.gov',
    clearance: 'secret',
  },
  {
    id: '94856110-bf76-459d-8896-aa0ff0d4b41a',
    firstName: 'Alyx',
    lastName: 'Vance',
    salary: 50000,
    email: 'alyx.vance@yahoo.com',
    clearance: 'civilian',
  },
  {
    id: 'c826a8ff-6d2d-47dd-a971-679b4122fa55',
    firstName: 'Isaac',
    lastName: 'Kleiner',
    salary: 50000,
    email: 'isaac.kleiner@mesa.mil.gov',
    clearance: 'civilian',
  },
  {
    id: '99656e0c-03c6-4e65-a3f2-dffdb5537acc',
    firstName: 'Father',
    lastName: 'Gregorii',
    salary: 50000,
    email: '-',
    clearance: 'civilian',
  },
];

const ExampleTableNoFooter = () => {
  const columns = useMemo(() => {
    const tableColumns = [
      columnHelper.accessor('id', {
        enableSorting: true,
        minSize: remToPx(9),
        size: remToPx(9),
        header: 'User ID',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Code>
            {[
              cell.row.original.id.slice(0, 5),
              cell.row.original.id.slice(-5),
            ].join('...')}
          </Code>
        ),
      }),
      columnHelper.accessor('firstName', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        header: 'First Name',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Tooltip label="First name">{cell.row.original.firstName}</Tooltip>
        ),
      }),
      columnHelper.accessor('lastName', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        header: 'Last Name',
        cell: (cell) => cell.row.original.lastName,
      }),
      columnHelper.accessor('email', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        header: 'Email',
        cell: (cell) => cell.row.original.email,
      }),
      columnHelper.accessor('clearance', {
        enableSorting: true,
        minSize: remToPx(11),
        size: remToPx(11),
        header: 'Government Clearance Level Long Header Name Test',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Tag
            size="sm"
            variant="solid-muted"
            colorScheme={(() => {
              if (cell.row.original.clearance === 'top-secret') return 'red';
              if (cell.row.original.clearance === 'secret') return 'orange';
              return 'green';
            })()}
          >
            <TagLeftIcon>
              <Icon icon="circle-x" />
            </TagLeftIcon>
            <TagLabel>{_.startCase(cell.row.original.clearance)}</TagLabel>
          </Tag>
        ),
      }),
    ];

    return tableColumns;
  }, []);

  const data = MOCK_TABLE_DATA;

  const table = useReactTable({
    data,
    columns,
    initialState: {
      columnPinning: { left: ['id'], right: ['clearance'] },
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Flex direction="column" gap="0.5rem">
      <Typography variant="section-title">Table with no footer</Typography>
      <TableBETA table={table} />
      <TanStackPaginationControl table={table} />
      <Typography variant="section-title">
        Table with no footer (fixed height)
      </Typography>
      <TableBETA table={table} FlexContainerProps={{ maxHeight: '20rem' }} />
      <TanStackPaginationControl table={table} />
    </Flex>
  );
};

const ExampleTableWithActionButton = () => {
  const columns = useMemo(() => {
    const tableColumns = [
      columnHelper.accessor('id', {
        enableSorting: true,
        minSize: remToPx(9),
        size: remToPx(9),
        header: 'User ID',
        footer: 'User ID',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Code>
            {[
              cell.row.original.id.slice(0, 5),
              cell.row.original.id.slice(-5),
            ].join('...')}
          </Code>
        ),
      }),
      columnHelper.accessor('firstName', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        header: 'First Name',
        footer: 'First Name',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Tooltip label="First name">{cell.row.original.firstName}</Tooltip>
        ),
      }),
      columnHelper.accessor('lastName', {
        header: 'Last Name',
        footer: 'Last Name',
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        cell: (cell) => cell.row.original.lastName,
      }),

      // This is a proof of concept for right-aligned cells, useful for numeric/dollar columns.
      // !TODO @eldarshamukhamedov Figure out how to encode alignment into the `ColumnDefs`
      columnHelper.accessor('salary', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        // eslint-disable-next-line react/display-name
        header: () => {
          return (
            <Typography
              inline
              fontWeight="medium"
              variant="base"
              TextProps={{ textAlign: 'right', width: '100%' }}
            >
              Salary
            </Typography>
          );
        },
        // eslint-disable-next-line react/display-name
        footer: () => {
          return (
            <Typography
              inline
              fontWeight="medium"
              variant="base"
              TextProps={{ textAlign: 'right', width: '100%' }}
            >
              Salary
            </Typography>
          );
        },
        // eslint-disable-next-line react/display-name
        cell: (cell) => {
          return (
            <Typography variant="base-muted" TextProps={{ textAlign: 'right' }}>
              {commonUtils.numberToCurrencyWithSymbol({
                number: cell.row.original.salary,
              })}
            </Typography>
          );
        },
      }),
      columnHelper.accessor('email', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        header: 'Email',
        footer: 'Email',
        cell: (cell) => cell.row.original.email,
      }),
      columnHelper.accessor('clearance', {
        enableSorting: true,
        minSize: remToPx(11),
        size: remToPx(11),
        header: 'Clearance',
        footer: 'Clearance',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Tag
            size="sm"
            variant="solid-muted"
            colorScheme={(() => {
              if (cell.row.original.clearance === 'top-secret') return 'red';
              if (cell.row.original.clearance === 'secret') return 'orange';
              return 'green';
            })()}
          >
            <TagLeftIcon>
              <Icon icon="circle-x" />
            </TagLeftIcon>
            <TagLabel>{_.startCase(cell.row.original.clearance)}</TagLabel>
          </Tag>
        ),
      }),
      columnHelper.display({
        id: 'actionButton',
        minSize: remToPx(4),
        size: remToPx(4),
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <IconButtonV2
            aria-label="More details"
            variant="v2-ghost"
            colorScheme="gray"
            size="sm"
            placement="left"
            icon={<Icon icon="chevron-right" />}
            IconButtonProps={{ as: RouterLink, to: `#${cell.row.original.id}` }}
          />
        ),
      }),
    ];

    return tableColumns;
  }, []);

  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'firstName', desc: true },
    { id: 'lastName', desc: true },
  ]);

  const data = MOCK_TABLE_DATA;

  const table = useReactTable({
    data,
    columns,
    enableMultiSort: true,
    state: { sorting },
    initialState: {
      columnPinning: { left: ['id'], right: ['actionButton'] },
      sorting,
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Flex direction="column" gap="0.5rem">
      <Typography variant="section-title">
        Table with pinned columns and action button
      </Typography>
      <TableBETA table={table} />
      <TanStackPaginationControl table={table} />
      <Typography variant="section-title">
        Table with pinned columns and action button (fixed height)
      </Typography>
      <TableBETA table={table} FlexContainerProps={{ maxHeight: '20rem' }} />
      <TanStackPaginationControl table={table} />
    </Flex>
  );
};

const ExampleTableClickableRow = () => {
  const navigate = useNavigate();

  const columns = useMemo(() => {
    const tableColumns = [
      columnHelper.accessor('id', {
        enableSorting: true,
        minSize: remToPx(9),
        size: remToPx(9),
        header: 'User ID',
        footer: 'User ID',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Code>
            {[
              cell.row.original.id.slice(0, 5),
              cell.row.original.id.slice(-5),
            ].join('...')}
          </Code>
        ),
      }),
      columnHelper.accessor('firstName', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        header: 'First Name',
        footer: 'First Name',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Tooltip label="First name">{cell.row.original.firstName}</Tooltip>
        ),
      }),
      columnHelper.accessor('lastName', {
        header: 'Last Name',
        footer: 'Last Name',
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        cell: (cell) => cell.row.original.lastName,
      }),
      columnHelper.accessor('email', {
        enableSorting: true,
        minSize: remToPx(15),
        size: remToPx(15),
        header: 'Email',
        footer: 'Email',
        cell: (cell) => cell.row.original.email,
      }),
      columnHelper.accessor('clearance', {
        enableSorting: true,
        minSize: remToPx(11),
        size: remToPx(11),
        header: 'Clearance',
        footer: 'Clearance',
        // eslint-disable-next-line react/display-name
        cell: (cell) => (
          <Tag
            variant="solid-muted"
            size="sm"
            colorScheme={(() => {
              if (cell.row.original.clearance === 'top-secret') return 'red';
              if (cell.row.original.clearance === 'secret') return 'orange';
              return 'green';
            })()}
            gap="0.5rem"
          >
            <Icon icon="circle-x" />
            <TagLabel>{_.startCase(cell.row.original.clearance)}</TagLabel>
          </Tag>
        ),
      }),
    ];

    return tableColumns;
  }, []);

  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'firstName', desc: true },
    { id: 'lastName', desc: true },
  ]);

  const data = MOCK_TABLE_DATA;

  const table = useReactTable({
    data,
    columns,
    enableMultiSort: true,
    state: { sorting },
    initialState: {
      columnPinning: { left: ['id'] },
      sorting,
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Flex direction="column" gap="0.5rem">
      <Typography variant="section-title">
        Table with pinned columns and clickable rows
      </Typography>
      <TableBETA
        table={table}
        onRowClick={(row) => navigate(`#${row.original.id}`)}
      />
      <TanStackPaginationControl table={table} />
      <Typography variant="section-title">
        Table with pinned columns and clickable rows (fixed height)
      </Typography>
      <TableBETA
        table={table}
        FlexContainerProps={{ maxHeight: '20rem' }}
        onRowClick={(row) => navigate(`#${row.original.id}`)}
      />
      <TanStackPaginationControl table={table} />
    </Flex>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    resetAfterCatch={false}
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <div>Uh oh</div>;
    }}
  >
    <PageTemplate title={COMPONENT_NAME}>
      <ExampleTableNoFooter />
      <ExampleTableWithActionButton />
      <ExampleTableClickableRow />
    </PageTemplate>
  </RouteErrorBoundary>
);
